import * as React from 'react';
import { OEEHistory } from '../api/dataTypes';
import { VictoryPie } from 'victory';
import { OEEDataGraph } from './oeeDataGraph';

interface status {
    value: number,
    units: string,
}

interface State {
    runTimeTotal: status,
    idleTimeTotal: status,
    cutLengthTotal: number,
    cycleCountTotal: number,
    utilizationTotal: number,
}

export interface Props {
    records: OEEHistory[] | null,
    startDate: Date;
    endDate: Date;
}

export class OEESummary extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            runTimeTotal: { value: 0, units: "min" },
            idleTimeTotal: { value: 0, units: "min" },
            cutLengthTotal: 0,
            cycleCountTotal: 0,
            utilizationTotal: 0,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps !== this.props ) {
            this.updateTotals();
        }
    }

    updateTotals = () => {
        if (this.props.records !== null) {
            let totalRunTime = Math.round(this.props.records.reduce((totalRunTime, record) => totalRunTime + record.runTime, 0) * 100) / 100;
            let totalIdleTime = Math.round(this.props.records.reduce((totalIdleTime, record) => totalIdleTime + record.idleTime, 0) * 100) / 100;

            let runTimeStat = { value: totalRunTime, units: "minutes" };
            let idleTimeStat = { value: totalIdleTime, units: "minutes" };

            if (totalRunTime > 60 && totalRunTime <= 1440) {
                runTimeStat = { value: Math.round(totalRunTime * 100 / 60) / 100, units: "hours" };
            } else if (totalRunTime > 1440) {
                runTimeStat = { value: Math.round(totalRunTime * 100 / 60) / 1440, units: "days" };
            }
            if (totalIdleTime > 60 && totalRunTime <= 1440) {
                idleTimeStat = { value: Math.round(totalIdleTime * 100 / 60) / 100, units: "hours" };
            } else if (totalIdleTime > 1440) {
                idleTimeStat = { value: Math.round(totalIdleTime * 100 / 60) / 1440, units: "days" };
            }
            
            this.setState({
                runTimeTotal: runTimeStat,
                idleTimeTotal: idleTimeStat,
                cycleCountTotal: this.props.records.reduce((totalCount, record) => totalCount + record.cycleCount, 0),
                utilizationTotal: Math.round(100 * totalRunTime / (totalIdleTime + totalRunTime)),
                cutLengthTotal: Math.round( 100 * this.props.records.reduce((totalLength, record) => totalLength + record.cutLengthTotal, 0)) / 100,
            })
        } else {
            this.setState({
                runTimeTotal: { value: 0, units: "min" },
                idleTimeTotal: { value: 0, units: "min" },
                cutLengthTotal: 0,
                cycleCountTotal: 0,
                utilizationTotal: 0,
            })
        }
    }

    render() {
        return (
            <div>
                {this.props.records !== null && 
                <div className="component-standard">
                    <div className="row" style={{ 'marginRight': '-22px' }}>
                        <div className="col-12 label-large text-dark-blue">
                            OEE Summary Over Selected Date Range:
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className={`col-8 w-100`}>
                            <div className="row content flex-container list-divider">
                                <div className="col label-medium">
                                    Run Time Total:
                                </div>
                                <div className="col">
                                    <div className="row">
                                        <span className={`text-bold ml-2`}>
                                            {this.state.runTimeTotal.value}
                                        </span>
                                        <span className="ml-2">
                                            {this.state.runTimeTotal.units}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="row content flex-container list-divider">
                                <div className="col label-medium">
                                    Idle Time Total:
                                </div>
                                <div className="col">
                                    <div className="row">
                                        <span className={`text-bold ml-2`}>
                                            {this.state.idleTimeTotal.value}
                                        </span>
                                        <span className="ml-2">
                                            {this.state.idleTimeTotal.units}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="row content flex-container list-divider">
                                <div className="col label-medium">
                                    Cycle Count Total:
                                </div>
                                <div className="col">
                                    <div className="row">
                                        <span className={`text-bold ml-2`}>
                                            {this.state.cycleCountTotal}
                                        </span>
                                        <span className="ml-2">
                                            cycles
                                    </span>
                                    </div>
                                </div>
                            </div>
                            <div className="row content flex-container list-divider">
                                <div className="col label-medium">
                                    Total Length Cut:
                                </div>
                                <div className="col">
                                    <div className="row">
                                        <span className={`text-bold ml-2`}>
                                            {this.state.cutLengthTotal}
                                        </span>
                                        <span className="ml-2">
                                            feet
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-4 align-self-center">
                            <VictoryPie
                                data={[{ y: this.state.runTimeTotal.units === "minutes" ? this.state.runTimeTotal.value : 
                                            this.state.runTimeTotal.units === "hours" ? this.state.runTimeTotal.value * 60 : 
                                            this.state.runTimeTotal.units === "days" ? this.state.runTimeTotal.value * 60 * 24 : 0,
                                            x: "Running" },
                                { y:    this.state.idleTimeTotal.units === "minutes" ? this.state.idleTimeTotal.value : 
                                        this.state.idleTimeTotal.units === "hours" ? this.state.idleTimeTotal.value * 60 : 
                                        this.state.idleTimeTotal.units === "days" ? this.state.idleTimeTotal.value * 60 * 24 : 0, 
                                        x: "Idle" }]}
                                colorScale={["#029c02", "#F58020"]}
                                labelRadius={50}
                                style={{ labels: { fill: 'white', fontSize: 24 } }}
                            />
                            <div className="text-bold align-self-center center-content">
                                {`${isNaN(this.state.utilizationTotal)? 0 : this.state.utilizationTotal}% Utilization`}
                            </div>
                        </div>
                    </div>
                </div>}
                <div className="component-standard">
                    <OEEDataGraph
                        data={this.props.records}
                        startDate={this.props.startDate}
                        endDate={this.props.endDate}
                    />
                </div>
            </div>

        );
    }
}